import React, { useCallback, useEffect, useState } from 'react';
import ConsoleShell from '../../components/admin2/ConsoleShell';
import { marketRpc } from '../../lib/marketDb';
import { productIdFrom } from '../../lib/marketAdminSizes';

// Catalog safety (owner, 2026-11-02 — PRICING_PLAN §2.1 / §2.3).
// Counterfeit review: listings whose brand is the maker, an «أصلية» claim for a famous
// brand, «لولو», "Gorilla" glass. Today's catalog stays visible while on this list;
// a NEW import that matches is hidden until decided. "Counterfeit" hides it for good
// (hold 'counterfeit_confirmed'); "Not counterfeit" takes it off the list.
// Compliance: the SABER / SFDA / CST notes come from rules; here staff force one on
// or off for a product (empty = the rule decides).

const T = {
  ar: {
    title: 'سلامة الكتالوج', sub: 'مراجعة المنتجات المقلّدة المحتملة، وملاحظات الشهادات (سابر / الغذاء والدواء / الاتصالات).',
    tabs: { review: 'مراجعة التقليد', compliance: 'ملاحظات الشهادات' },
    signal: { maker_brand: 'العلامة هي الصانع', original_claim: 'ادعاء «أصلي» لماركة مشهورة', brand_short_form: 'اختصار ماركة (لولو)', gorilla_glass: 'ادعاء زجاج Gorilla' },
    hiddenNew: 'منتج جديد — مخفي حتى القرار', visible: 'ظاهر للعملاء', fake: 'مقلّد — إخفاء', ok: 'ليس مقلّدًا', all: 'تحديد الكل', none: 'لا شيء للمراجعة',
    product: 'رابط المنتج أو رقمه', rule: 'حسب القاعدة', on: 'يظهر', off: 'لا يظهر', note: 'ملاحظة', save: 'حفظ', saved: 'تم الحفظ', noProduct: 'لم أجد رقم منتج',
    cHint: 'اترك الحقل «حسب القاعدة» ليقرر النظام: سابر للأجهزة الكهربائية والإلكترونية، الغذاء والدواء لمستحضرات التجميل والطبية، الاتصالات للجوالات وأجهزة الاتصال.',
    saber: 'سابر', sfda: 'الغذاء والدواء', cst: 'الاتصالات', done: (n) => `تم: ${n}`,
  },
  en: {
    title: 'Catalog safety', sub: 'Review possible counterfeits, and the certificate notes (SABER / SFDA / CST).',
    tabs: { review: 'Counterfeit review', compliance: 'Certificate notes' },
    signal: { maker_brand: 'Brand is the maker', original_claim: '"Original" claim for a famous brand', brand_short_form: 'Brand short form (Lulu)', gorilla_glass: 'Gorilla glass claim' },
    hiddenNew: 'New import — hidden until decided', visible: 'Visible to customers', fake: 'Counterfeit — hide', ok: 'Not counterfeit', all: 'Select all', none: 'Nothing to review',
    product: 'Product link or id', rule: 'By rule', on: 'Show', off: 'Hide', note: 'Note', save: 'Save', saved: 'Saved', noProduct: 'No product id found',
    cHint: 'Leave "By rule" for the system to decide: SABER for electrical/electronic goods, SFDA for cosmetics and medical, CST for phones and telecom devices.',
    saber: 'SABER', sfda: 'SFDA', cst: 'CST', done: (n) => `Done: ${n}`,
  },
};

export default function ConsoleMarketSafety({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const c = T[lang] || T.ar;
  const [tab, setTab] = useState('review');
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState({});
  const [toast, setToast] = useState('');
  const [cf, setCf] = useState({ product: '', saber: '', sfda: '', cst: '', note: '' });
  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 2600); };

  const load = useCallback(async () => {
    setRows(null); setSel({});
    // the review list today (visible) and the new imports held hidden
    const [a, b] = await Promise.all([
      marketRpc('admin_market_holds', { p_reason: 'counterfeit_review', p_kind: 'review', p_limit: 200 }),
      marketRpc('admin_market_holds', { p_reason: 'counterfeit_review', p_kind: 'hide', p_limit: 200 }),
    ]);
    if (!a.ok || !b.ok) flash((a.error || b.error) ?? '');
    const all = [...(b.data?.rows || []).map((r) => ({ ...r, hiddenNew: true })), ...(a.data?.rows || [])];
    setRows(all);
  }, []);
  useEffect(() => { if (tab === 'review') load(); }, [tab, load]);

  const decide = async (ids, counterfeit) => {
    if (!ids.length) return;
    const r = await marketRpc('admin_market_counterfeit_decide', { p_product_ids: ids, p_counterfeit: counterfeit, p_note: null });
    flash(r.ok ? c.done(r.data) : r.error); if (r.ok) load();
  };
  const signalOf = (r) => (r.holds || []).find((h) => h.reason === 'counterfeit_review')?.evidence?.signal;
  const title = (r) => (isAr ? r.title_ar || r.title_en : r.title_en || r.title_ar);
  const tri = (v) => (v === 'on' ? true : v === 'off' ? false : null);
  const saveCompliance = async () => {
    const id = productIdFrom(cf.product);
    if (!id) { flash(c.noProduct); return; }
    const r = await marketRpc('admin_market_compliance_set', { p_product_id: id, p_saber: tri(cf.saber), p_sfda: tri(cf.sfda), p_cst: tri(cf.cst), p_note: cf.note || null });
    flash(r.ok ? c.saved : r.error);
    if (r.ok) setCf({ product: '', saber: '', sfda: '', cst: '', note: '' });
  };
  const picked = Object.keys(sel).filter((k) => sel[k]);
  const pick3 = (k) => (
    <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, gap: 4 }}>{c[k]}
      <select className="ac-input" value={cf[k]} onChange={(e) => setCf({ ...cf, [k]: e.target.value })}>
        <option value="">{c.rule}</option><option value="on">{c.on}</option><option value="off">{c.off}</option>
      </select>
    </label>
  );

  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="market-safety">
      <div className="ac-page" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
        <div className="ac-page-head"><div><h1 className="ac-h1">{c.title}</h1><p className="ac-sub">{c.sub}</p></div></div>
        <div className="ac-chiprow" style={{ marginBottom: 12 }}>
          {Object.keys(c.tabs).map((k) => <button key={k} type="button" data-safety-tab={k} className={`ac-chip${tab === k ? ' on' : ''}`} onClick={() => setTab(k)}>{c.tabs[k]}</button>)}
        </div>

        {tab === 'review' && (
          <div className="ac-card">
            {!rows ? <p className="ac-sub">…</p> : !rows.length ? <p className="ac-sub">{c.none}</p> : <>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <button type="button" className="ac-btn ac-btn-sm" onClick={() => setSel(Object.fromEntries(rows.map((r) => [r.id, true])))}>{c.all}</button>
                <button type="button" className="ac-btn ac-btn-sm" disabled={!picked.length} onClick={() => decide(picked, true)}>{c.fake} ({picked.length})</button>
                <button type="button" className="ac-btn ac-btn-sm" disabled={!picked.length} onClick={() => decide(picked, false)}>{c.ok} ({picked.length})</button>
              </div>
              <table className="ac-table" data-counterfeit-review={rows.length}>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td><input type="checkbox" checked={!!sel[r.id]} onChange={(e) => setSel({ ...sel, [r.id]: e.target.checked })} /></td>
                      <td><a href={`/market/${r.id}`} target="_blank" rel="noreferrer">{title(r)}</a></td>
                      <td>{c.signal[signalOf(r)] || signalOf(r) || '—'}</td>
                      <td>{r.hiddenNew ? c.hiddenNew : c.visible}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button type="button" className="ac-btn ac-btn-sm" onClick={() => decide([r.id], true)}>{c.fake}</button>{' '}
                        <button type="button" className="ac-btn ac-btn-sm" onClick={() => decide([r.id], false)}>{c.ok}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>}
          </div>
        )}

        {tab === 'compliance' && (
          <div className="ac-card">
            <p className="ac-sub">{c.cHint}</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
              <input className="ac-input" dir="ltr" style={{ flex: '2 1 260px' }} placeholder={c.product} value={cf.product} onChange={(e) => setCf({ ...cf, product: e.target.value })} />
              {pick3('saber')}{pick3('sfda')}{pick3('cst')}
              <input className="ac-input" style={{ flex: '1 1 180px' }} placeholder={c.note} value={cf.note} onChange={(e) => setCf({ ...cf, note: e.target.value })} />
              <button type="button" className="ac-btn" onClick={saveCompliance}>{c.save}</button>
            </div>
          </div>
        )}
        {toast && <div className="ac-toast" role="status">{toast}</div>}
      </div>
    </ConsoleShell>
  );
}
